import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Typography, Chip, MenuItem, Select, useTheme, Divider } from '@material-ui/core';
import newLesionIcon from '../../../../../../resources/icons/new_lesion.png';
import { TYPE_OPTIONS } from '../constants/constants';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { isMissedFinding } from '../../../../../../helpers/diagnostics/detectionOrigin';

const TypeChip = ({ lesionAllInfo, isExpanded = false, nested = false, changeType, smallScreen = false }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const [editingTypeRowId, setEditingTypeRowId] = useState(null);

  // Extract data from lesionAllInfo
  const module_type = lesionAllInfo?.module_type;
  const missingLesions = isMissedFinding(lesionAllInfo);
  const isClustered = lesionAllInfo?.clustered;
  const isMatchedMainRow = lesionAllInfo?.isMatchedMainRow;
  const uniqueId = lesionAllInfo?.finding_id;
  const lesionId = lesionAllInfo?.object_id;

  // Extract matched lesion info for isNewLesion
  const matched = lesionAllInfo?.matched_lesions || lesionAllInfo?.match;
  const matchedLesions = lesionAllInfo?.matched_lesions;
  const matchedLesionAllInfo = matchedLesions?.[1] || matchedLesions?.[0];
  const isNewLesion = matched ? matchedLesionAllInfo?.is_new && lesionAllInfo?.is_new : lesionAllInfo?.is_new;

  // Memoized values
  const isEditing = useMemo(() => {
    return editingTypeRowId && editingTypeRowId === uniqueId;
  }, [editingTypeRowId, uniqueId]);

  // Event handlers
  const handleSelectClose = useCallback(
    (event, reason) => {
      setEditingTypeRowId(null);
    },
    [setEditingTypeRowId],
  );

  const handleOptionClick = useCallback(
    (lesionAllInfo, option, event) => {
      event?.stopPropagation();
      changeType(lesionAllInfo, option);
      setEditingTypeRowId(null);
    },
    [changeType, setEditingTypeRowId],
  );

  const handleChipClick = useCallback(
    (e) => {
      e.stopPropagation();
      requestAnimationFrame(() => {
        setEditingTypeRowId(uniqueId);
      });
    },
    [uniqueId],
  );

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  // Render logic

  if (isExpanded && nested && module_type === 'opacities') {
    return <div className={classes.empty_cell_container} />;
  }

  if (isEditing) {
    return (
      <Select
        variant='outlined'
        open={isEditing}
        onClose={handleSelectClose}
        value={
          lesionAllInfo?.new_type
            ? lesionAllInfo?.new_type
            : module_type === 'microcalc'
              ? 'microcalc'
              : 'opacities'
        }
        className={`${classes.selectNoBorder} row-type-change-select`}
        MenuProps={{
          disablePortal: false,
          disableScrollLock: true,
          PaperProps: {
            style: {
              marginTop: 40,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}>
        {TYPE_OPTIONS.map((option) => (
          <MenuItem
            key={option}
            value={option}
            className={classes.chip_container}
            style={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              backgroundColor: lesionAllInfo?.new_type === option ? 'rgba(55, 87, 105, 0.3)' : 'transparent',
            }}
            onClick={(event) => handleOptionClick(lesionAllInfo, option, event)}
            disabled={missingLesions && module_type === 'opacities' ? false : module_type === option}>
            <Typography
              className={classes.chip_texts}
              style={{
                textAlign: 'left',
                width: '100%',
              }}>
              {t(`diagnose.type_${option}`) || option}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Default chip display
  const getChipLabel = () => {
    if (module_type === 'microcalc') {
      return t('projections.microcalcifications');
    }

    if (missingLesions && module_type === 'opacities' && !nested) {
      return t('diagnose.prior_finding');
    }

    if (module_type === 'opacities' && nested) {
      return t('revision.opacity');
    }

    if (isMatchedMainRow) {
      return lesionAllInfo?.new_type
        ? `${t(`diagnose.${lesionAllInfo.new_type}`)}`
        : module_type === 'microcalc'
          ? t('projections.microcalcifications')
          : t('projections.opacity');
    }

    return lesionAllInfo?.new_type
      ? `${t(`diagnose.${lesionAllInfo.new_type}`)}`
      : module_type === 'microcalc'
        ? t('projections.microcalcifications')
        : t('projections.opacity');
  };

  const getChipClassName = () => {
    if (module_type === 'microcalc') {
      return `${classes.type_chip} ${classes.microcalc_type_chips}`;
    }

    if (isClustered) {
      return `${classes.type_chip} ${classes.missing_type_chips}`;
    }

    if (lesionAllInfo?.new_type) {
      return `${classes.type_chip} ${classes.diagnostics_new_type_chips}`;
    }

    return `${classes.type_chip} ${classes.diagnostics_type_chips}`;
  };

  return (
    <>
      {missingLesions && module_type === 'opacities' && nested ? (
        <div className={classes.empty_cell_container} />
      ) : (
        <>
          <Tooltip
            arrow
            title={
              missingLesions && module_type === 'opacities' ? (
                <Typography className={classes.tooltip_text}>
                  {t('diagnose.prior_finding_explanation')}
                </Typography>
              ) : (
                <>
                  <Typography className={classes.tooltip_text}>
                    {isClustered && module_type !== 'microcalc'
                      ? t('diagnose.is_clustered')
                      : t(`revision.type_${module_type}`)}
                  </Typography>
                  <Divider style={{ margin: '4px 0' }} />

                  <Typography className={classes.tooltip_text} style={{ fontSize: '10px' }}>
                    ID: {isMatchedMainRow ? lesionId : uniqueId || lesionId}
                  </Typography>
                  {!isClustered && (
                    <Typography className={classes.tooltip_text}>{t('ResultView.change')}</Typography>
                  )}
                </>
              )
            }>
            {(isMatchedMainRow || isClustered) && isExpanded ? (
              <Chip
                clickable={false}
                label={getChipLabel()}
                className={classes.type_chip}
                size='small'
                onClick={() => {}}
                onPointerDown={() => {}}
              />
            ) : (
              <Chip
                avatar={
                  isClustered || isMatchedMainRow ? undefined : <ArrowDropDownIcon style={{ margin: 0 }} />
                }
                clickable={isClustered || isMatchedMainRow ? false : true}
                label={getChipLabel()}
                className={getChipClassName()}
                size='small'
                onClick={isClustered ? () => {} : handleChipClick}
                onPointerDown={isClustered ? () => {} : handlePointerDown}
              />
            )}
          </Tooltip>

          {isNewLesion && (
            <Tooltip
              arrow
              title={
                <Typography className={classes.tooltip_text}>
                  {t('diagnose.newLesion')} - {t('diagnose.new_lesion_info')}
                </Typography>
              }>
              <img
                src={newLesionIcon}
                alt='newLesion'
                style={{
                  cursor: 'help',
                  width: '25px',
                  height: '23px',
                  position: 'absolute',
                  right: smallScreen ? '0' : '14px',
                  top: '-6px',
                }}
              />
            </Tooltip>
          )}
        </>
      )}
    </>
  );
};

export default memo(TypeChip);
